<template>
  <LoadingSpinner :loading='loading'> </LoadingSpinner>

  <div class="container mt-4 mb-4">
    <h3 class="mb-4">Smart hentetid innsikt</h3>

    <div class="filter-grid">
      <div v-if="hasMultiLocations()" class="filter-item">
        <label class="filter-label">For avdeling</label>
        <select class="form-select" v-model="locationId" @change="loadInsight()">
          <option v-for="(location, index) in locations" :key="index" :value="location.id">{{ location.name }}</option>
        </select>
      </div>

      <div class="filter-item">
        <label class="filter-label">Fra</label>
        <div class="date-time-row">
          <SelectDate ref="fromDate" :selectedDate="fromDateString"/>
          <TimePicker v-model="fromTime" :defaultHour="0" :defaultMinute="0" :stepMinutes="15"/>
        </div>
      </div>
      <div class="filter-item">
        <label class="filter-label">Til</label>
        <div class="date-time-row">
          <SelectDate ref="toDate" :selectedDate="toDateString"/>
          <TimePicker v-model="toTime" :defaultHour="23" :defaultMinute="59" :stepMinutes="15"/>
        </div>
      </div>
      <div class="filter-item filter-btn-item">
        <button type="button" class="bo_btn" @click="loadInsight()">Hent data</button>
      </div>
    </div>

    <!-- Location config -->
    <div v-if="insight.locationConfig" class="config-panel mb-4">
      <h5>Konfigurasjon: {{ insight.locationConfig.locationName }}</h5>
      <div class="config-grid">
        <div class="config-item">
          <span class="config-label">Basis hentetid</span>
          <span class="config-value">{{ insight.locationConfig.earliestPickupTime }} min</span>
        </div>
        <div class="config-item">
          <span class="config-label">Kr per minutt</span>
          <span class="config-value">{{ insight.locationConfig.smartPickupTimeKrPerMinute }} kr/min</span>
        </div>
        <div class="config-item">
          <span class="config-label">Maks hentetid</span>
          <span class="config-value">{{ insight.locationConfig.smartPickupTimeMaxMinutes }} min</span>
        </div>
        <div class="config-item">
          <span class="config-label">Slot-størrelse</span>
          <span class="config-value">{{ insight.locationConfig.earliestPickupTimeStepMinutes }} min</span>
        </div>
        <div class="config-item">
          <span class="config-label">Slot-kapasitet</span>
          <span class="config-value">{{ insight.locationConfig.slotCapacityKr }} kr</span>
        </div>
        <div class="config-item">
          <span class="config-label">Manuell modus</span>
          <span class="config-value" :class="insight.locationConfig.useManualPickupOrDeliveryTime ? 'text-warning' : ''">
            {{ insight.locationConfig.useManualPickupOrDeliveryTime ? 'JA' : 'Nei' }}
          </span>
        </div>
        <div class="config-item">
          <span class="config-label">Intent-vekt</span>
          <span class="config-value">{{ Math.round((insight.locationConfig.smartPickupTimeIntentWeight || 0.8) * 100) }}%</span>
        </div>
        <div class="config-item">
          <span class="config-label">Nåværende kø (ordrer)</span>
          <span class="config-value">{{ insight.locationConfig.currentActiveQueueValueKr }} kr</span>
        </div>
        <div class="config-item">
          <span class="config-label">Nåværende intents</span>
          <span class="config-value">{{ insight.locationConfig.currentActiveIntentsValueKr || 0 }} kr (vektet: {{ insight.locationConfig.currentWeightedIntentsValueKr || 0 }} kr)</span>
        </div>
        <div class="config-item">
          <span class="config-label">Total kø</span>
          <span class="config-value">{{ insight.locationConfig.currentTotalQueueValueKr || insight.locationConfig.currentActiveQueueValueKr }} kr ({{ insight.locationConfig.currentQueueWaitMinutes }} min)</span>
        </div>
        <div v-if="insight.locationConfig.smartPickupTimeNotes" class="config-item config-item-wide">
          <span class="config-label">Notater</span>
          <span class="config-value">{{ insight.locationConfig.smartPickupTimeNotes }}</span>
        </div>
      </div>
    </div>

    <!-- Orders -->
    <div v-if="!loading && insight.orders && insight.orders.length === 0" class="text-muted mt-4">
      Ingen ordrer funnet i valgt periode.
    </div>

    <div v-if="insight.orders && insight.orders.length > 0">
      <div class="d-flex align-items-center mb-3">
        <h5 class="mb-0">{{ insight.orders.length }} ordrer ({{ getQueriedRange() }})</h5>
        <a @click="exportPdf()" class="pdf-icon ms-3">
          <img src="@/assets/images/pdf.png" alt="PDF" height="32" />
        </a>
      </div>

      <div class="order-list">
        <div v-for="(order, index) in insight.orders" :key="index"
             class="order-card" :class="getOrderCardClass(order)">

          <!-- Order header -->
          <div class="order-header" @click="toggleExpand(index)">
            <div class="order-header-left">
              <span class="order-id">{{ order.orderId }}</span>
              <span class="order-channel badge-channel">{{ order.channel }}</span>
              <span class="order-type badge-type">{{ order.orderType }}</span>
            </div>
            <div class="order-header-right">
              <span class="order-amount">{{ formatAmount(order.totalAmount) }} kr</span>
              <span class="order-wait" :class="getWaitClass(order)">{{ order.actualWaitMinutes }} min ventetid</span>
              <span class="expand-icon">{{ expandedOrders[index] ? '▲' : '▼' }}</span>
            </div>
          </div>

          <!-- Order times -->
          <div class="order-times">
            <span>Bestilt: {{ formatDate(order.orderedTime) }}</span>
            <span>Henting: {{ formatDate(order.pickupOrDeliveryTime) }}</span>
            <span v-if="order.name">{{ order.name }}</span>
          </div>

          <!-- Product summary -->
          <div v-if="order.cartItemDetails && order.cartItemDetails.length > 0" class="order-products">
            {{ order.cartItemDetails.map(item => item.count + 'x ' + item.itemName).join(', ') }}
          </div>

          <!-- Expanded detail -->
          <div v-if="expandedOrders[index]" class="order-detail">

            <!-- Calculation breakdown -->
            <div v-if="order.calculation" class="calc-section">
              <h6>Beregning</h6>
              <div class="calc-grid">
                <div class="calc-row">
                  <span class="calc-label">Basis hentetid</span>
                  <span class="calc-value">{{ order.calculation.basePickupTime }} min</span>
                </div>
                <div class="calc-row">
                  <span class="calc-label">Kjøkken tilberedningstid</span>
                  <span class="calc-value">{{ order.calculation.kitchenPrepTime }} min</span>
                </div>
                <div v-if="order.calculation.totalPrepTimeMinutes !== undefined" class="calc-row calc-sub">
                  <span class="calc-label">↳ Fra prepTimeMinutes</span>
                  <span class="calc-value">{{ order.calculation.totalPrepTimeMinutes }} min</span>
                </div>
                <div v-if="order.calculation.priceBasedPrepTime !== undefined" class="calc-row calc-sub">
                  <span class="calc-label">↳ Fra pris ({{ order.calculation.orderValueWithoutPrepTime }} kr / {{ order.calculation.krPerMinute }} kr/min)</span>
                  <span class="calc-value">{{ order.calculation.priceBasedPrepTime }} min</span>
                </div>
                <div v-if="order.calculation.fallbackPriceBasedPrepTime !== undefined" class="calc-row calc-sub">
                  <span class="calc-label">↳ Fallback pris-basert</span>
                  <span class="calc-value">{{ order.calculation.fallbackPriceBasedPrepTime }} min</span>
                </div>
                <div v-if="order.calculation.readyMadePrepTimeMinutes > 0" class="calc-row">
                  <span class="calc-label">ReadyMade tilberedningstid</span>
                  <span class="calc-value">{{ order.calculation.readyMadePrepTimeMinutes }} min</span>
                </div>
                <div class="calc-row">
                  <span class="calc-label">Har kjøkkenvarer</span>
                  <span class="calc-value">{{ order.calculation.hasKitchenItems ? 'Ja' : 'Nei' }}</span>
                </div>
                <div class="calc-row">
                  <span class="calc-label">Køverdi ved bestillingstidspunkt</span>
                  <span class="calc-value">{{ order.calculation.queueValueAtOrderTimeKr }} kr</span>
                </div>
                <div class="calc-row">
                  <span class="calc-label">Køventetid</span>
                  <span class="calc-value">{{ order.calculation.queueWaitMinutes }} min</span>
                </div>
                <div class="calc-row calc-formula">
                  <span class="calc-label">Formel</span>
                  <span class="calc-value">{{ order.calculation.formula }}</span>
                </div>
                <div class="calc-row">
                  <span class="calc-label">Før avrunding</span>
                  <span class="calc-value">{{ order.calculation.beforeRounding }} min</span>
                </div>
                <div class="calc-row calc-result">
                  <span class="calc-label">Beregnet hentetid</span>
                  <span class="calc-value">{{ order.calculation.afterRounding }} min</span>
                </div>
                <div class="calc-row" :class="order.actualWaitMinutes !== order.calculation.afterRounding ? 'calc-mismatch' : ''">
                  <span class="calc-label">Faktisk ventetid</span>
                  <span class="calc-value">{{ order.actualWaitMinutes }} min</span>
                </div>
              </div>

              <!-- Slot check -->
              <div v-if="order.calculation.slotCheck" class="slot-section mt-3">
                <h6>Slot-kapasitet</h6>
                <div class="slot-bar-container">
                  <div class="slot-bar-bg">
                    <div class="slot-bar-fill" :style="{ width: Math.min(order.calculation.slotCheck.slotUtilizationPercent, 100) + '%' }"
                         :class="order.calculation.slotCheck.overloaded ? 'slot-overloaded' : ''">
                    </div>
                  </div>
                  <span class="slot-bar-label">{{ order.calculation.slotCheck.slotUtilizationPercent }}%</span>
                </div>
                <div class="calc-grid mt-2">
                  <div class="calc-row">
                    <span class="calc-label">Slot</span>
                    <span class="calc-value">{{ formatTime(order.calculation.slotCheck.slotStart) }} – {{ formatTime(order.calculation.slotCheck.slotEnd) }}</span>
                  </div>
                  <div class="calc-row">
                    <span class="calc-label">Verdi i slot</span>
                    <span class="calc-value">{{ order.calculation.slotCheck.slotValueKr }} kr</span>
                  </div>
                  <div class="calc-row">
                    <span class="calc-label">Slot-kapasitet</span>
                    <span class="calc-value">{{ order.calculation.slotCheck.slotCapacityKr }} kr</span>
                  </div>
                  <div class="calc-row">
                    <span class="calc-label">Denne bestilling</span>
                    <span class="calc-value">{{ order.calculation.slotCheck.thisOrderKr }} kr</span>
                  </div>
                  <div class="calc-row" :class="order.calculation.slotCheck.overloaded ? 'calc-mismatch' : ''">
                    <span class="calc-label">Overbelastet</span>
                    <span class="calc-value">{{ order.calculation.slotCheck.overloaded ? 'JA' : 'Nei' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Cart items breakdown -->
            <div v-if="order.cartItemDetails && order.cartItemDetails.length > 0" class="cart-section mt-3">
              <h6>Ordrebeskrivelse</h6>
              <table class="cart-table">
                <thead>
                  <tr>
                    <th>Produkt</th>
                    <th>Antall</th>
                    <th>Pris</th>
                    <th>PrepTime</th>
                    <th>Beregningsmetode</th>
                    <th>Bidrag</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, i) in order.cartItemDetails" :key="i" :class="'cat-' + item.category">
                    <td>{{ item.itemName }}</td>
                    <td>{{ item.count }}</td>
                    <td>{{ item.price }} kr</td>
                    <td>{{ item.prepTimeMinutes > 0 ? item.prepTimeMinutes + ' min' : '-' }}</td>
                    <td>
                      <span class="item-category-badge" :class="'badge-' + item.category">{{ getCategoryLabel(item.category) }}</span>
                    </td>
                    <td>{{ item.contributionMinutes }} min{{ item.contributionKr ? ' (' + item.contributionKr + ' kr)' : '' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import authHeader from "@/services/auth/AuthHeader";
  import axios from "axios";
  import moment from "moment";
  import pdfMake from 'pdfmake/build/pdfmake';
  import pdfFonts from 'pdfmake/build/vfs_fonts';
  import LoadingSpinner from "@/components/other/LoadingSpinner";
  import SelectDate from "@/components/other/SelectDate";
  import TimePicker from "@/components/other/TimePicker";
  import LocationUtil from "@/util/LocationUtil";
  import LocationService from "@/services/location/LocationService";
  import MathUtil from "@/util/MathUtil";

  pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts;

  export default {
    components: {LoadingSpinner, SelectDate, TimePicker},
    data() {
      return {
        loading: true,
        locationId: '',
        locations: [],
        insight: {},
        expandedOrders: {},
        fromDateString: moment().format('YYYY-MM-DD'),
        toDateString: moment().format('YYYY-MM-DD'),
        fromTime: '00:00',
        toTime: '23:59',
        queriedFromDate: '',
        queriedFromTime: '',
        queriedToDate: '',
        queriedToTime: ''
      };
    },
    mounted() {
      LocationService.listLocations().then(
          (response) => {
            this.locations = response.data;
            this.locationId = this.locations.length ? this.locations[0].id : '';
            this.loadInsight();
          },
          (error) => {
            console.log(error);
            this.loading = false;
          }
      );
    },
    methods: {
      hasMultiLocations(){
        return LocationUtil.hasMultiLocations();
      },
      loadInsight(){
        this.loading = true;
        this.expandedOrders = {};
        let fromDate = this.$refs.fromDate ? this.$refs.fromDate.getSelectedDate() : this.fromDateString;
        let toDate = this.$refs.toDate ? this.$refs.toDate.getSelectedDate() : this.toDateString;
        this.queriedFromDate = fromDate;
        this.queriedFromTime = this.fromTime;
        this.queriedToDate = toDate;
        this.queriedToTime = this.toTime;
        let user = JSON.parse(localStorage.getItem('user'));
        let url = '/api/superadmin/system/getSmartPickupTimeInsight?merchant=' + user.merchant
            + '&locationId=' + this.locationId
            + '&fromDate=' + fromDate + '&fromTime=' + encodeURIComponent(this.fromTime)
            + '&toDate=' + toDate + '&toTime=' + encodeURIComponent(this.toTime);
        let basicAuth = authHeader();
        axios.get(url, { headers: basicAuth }).then(
            (response) => {
              this.insight = response.data;
              this.loading = false;
            },
            (error) => {
              console.log(error);
              this.loading = false;
            }
        );
      },
      toggleExpand(index) {
        this.expandedOrders[index] = !this.expandedOrders[index];
      },
      getOrderCardClass(order) {
        if (!order.calculation) return '';
        if (order.calculation.slotCheck && order.calculation.slotCheck.overloaded) return 'border-status-overloaded';
        if (order.actualWaitMinutes !== undefined && order.actualWaitMinutes !== order.calculation.afterRounding) return 'border-status-adjusted';
        return 'border-status-ok';
      },
      getWaitClass(order) {
        if (!order.calculation) return '';
        if (order.calculation.slotCheck && order.calculation.slotCheck.overloaded) return 'text-danger';
        return '';
      },
      getCategoryLabel(category) {
        if (category === 'kitchenPrepTime') return 'Kjøkken';
        if (category === 'readyMade') return 'Ferdiglaget';
        if (category === 'priceBasedEstimate') return 'Pris-basert';
        return category;
      },
      getQueriedRange(){
        let from = moment(this.queriedFromDate, 'YYYY-MM-DD').format('DD.MM.YYYY');
        let to = moment(this.queriedToDate, 'YYYY-MM-DD').format('DD.MM.YYYY');
        let isFullDay = this.queriedFromTime === '00:00' && this.queriedToTime === '23:59';
        if (from === to && isFullDay) {
          return from;
        }
        if (from === to) {
          return from + ' ' + this.queriedFromTime + ' – ' + this.queriedToTime;
        }
        if (isFullDay) {
          return from + ' – ' + to;
        }
        return from + ' ' + this.queriedFromTime + ' – ' + to + ' ' + this.queriedToTime;
      },
      formatAmount(amount){
        return MathUtil.displayTwoDecimals(amount);
      },
      formatDate(dateStr){
        if(!dateStr) return '';
        let date = new Date(dateStr);
        let day = String(date.getDate()).padStart(2, '0');
        let month = String(date.getMonth() + 1).padStart(2, '0');
        let hours = String(date.getHours()).padStart(2, '0');
        let minutes = String(date.getMinutes()).padStart(2, '0');
        return day + '.' + month + ' kl. ' + hours + ':' + minutes;
      },
      formatTime(dateStr){
        if(!dateStr) return '';
        let date = new Date(dateStr);
        let hours = String(date.getHours()).padStart(2, '0');
        let minutes = String(date.getMinutes()).padStart(2, '0');
        return hours + ':' + minutes;
      },
      exportPdf(){
        const config = this.insight.locationConfig;
        const orders = this.insight.orders;
        const range = this.getQueriedRange();
        const separator = { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, dash: { length: 3, space: 3 }, lineWidth: 0.5 }], margin: [0, 5, 0, 5] };
        const content = [];

        // Title
        content.push({ text: 'Smart hentetid innsikt', style: 'title', alignment: 'center' });
        content.push({ text: config.locationName, alignment: 'center', margin: [0, 0, 0, 5] });
        content.push(separator);

        // Config table
        content.push({ text: 'Konfigurasjon', style: 'sectionTitle', margin: [0, 5, 0, 5] });
        content.push({
          table: {
            widths: ['*', 'auto'],
            body: [
              [{ text: 'Basis hentetid', style: 'tableCell' }, { text: config.earliestPickupTime + ' min', style: 'tableCell', alignment: 'right' }],
              [{ text: 'Kr per minutt', style: 'tableCell' }, { text: config.smartPickupTimeKrPerMinute + ' kr/min', style: 'tableCell', alignment: 'right' }],
              [{ text: 'Maks hentetid', style: 'tableCell' }, { text: config.smartPickupTimeMaxMinutes + ' min', style: 'tableCell', alignment: 'right' }],
              [{ text: 'Slot-størrelse', style: 'tableCell' }, { text: config.earliestPickupTimeStepMinutes + ' min', style: 'tableCell', alignment: 'right' }],
              [{ text: 'Slot-kapasitet', style: 'tableCell' }, { text: config.slotCapacityKr + ' kr', style: 'tableCell', alignment: 'right' }],
              [{ text: 'Manuell modus', style: 'tableCell' }, { text: config.useManualPickupOrDeliveryTime ? 'JA' : 'Nei', style: 'tableCell', alignment: 'right' }],
              [{ text: 'Intent-vekt', style: 'tableCell' }, { text: Math.round((config.smartPickupTimeIntentWeight || 0.8) * 100) + '%', style: 'tableCell', alignment: 'right' }],
              [{ text: 'Nåværende kø (ordrer)', style: 'tableCell' }, { text: config.currentActiveQueueValueKr + ' kr', style: 'tableCell', alignment: 'right' }],
              [{ text: 'Nåværende intents', style: 'tableCell' }, { text: (config.currentActiveIntentsValueKr || 0) + ' kr (vektet: ' + (config.currentWeightedIntentsValueKr || 0) + ' kr)', style: 'tableCell', alignment: 'right' }],
              [{ text: 'Total kø', style: 'tableCell' }, { text: (config.currentTotalQueueValueKr || config.currentActiveQueueValueKr) + ' kr (' + config.currentQueueWaitMinutes + ' min)', style: 'tableCell', alignment: 'right' }],
              [{ text: 'Notater', style: 'tableCell' }, { text: config.smartPickupTimeNotes || '-', style: 'tableCell', alignment: 'right' }]
            ]
          },
          layout: 'lightHorizontalLines'
        });
        content.push(separator);

        // Orders summary on first page
        content.push({ text: orders.length + ' ordrer (' + range + ')', style: 'sectionTitle', margin: [0, 10, 0, 5] });

        for (let i = 0; i < orders.length; i++) {
          const order = orders[i];
          const calc = order.calculation || {};

          // Each order starts on a new page
          content.push({ text: '', pageBreak: 'before' });
          content.push({ text: 'Ordre ' + (i + 1) + ' av ' + orders.length, style: 'small', alignment: 'right', margin: [0, 0, 0, 5] });

          // Order header
          content.push({
            table: {
              widths: ['auto', '*', 'auto'],
              body: [[
                { text: order.orderId, style: 'tableBold' },
                { text: order.channel + ' / ' + order.orderType, style: 'tableCell' },
                { text: this.formatAmount(order.totalAmount) + ' kr', style: 'tableBold', alignment: 'right' }
              ]]
            },
            layout: 'noBorders',
            fillColor: 'var(--bo-surface-2)',
            margin: [0, 0, 0, 2]
          });

          content.push({
            columns: [
              { text: 'Bestilt: ' + this.formatDate(order.orderedTime), style: 'small' },
              { text: 'Henting: ' + this.formatDate(order.pickupOrDeliveryTime), style: 'small' },
              { text: (order.name || ''), style: 'small', alignment: 'right' }
            ],
            margin: [0, 0, 0, 2]
          });

          // Product summary
          if (order.cartItemDetails && order.cartItemDetails.length > 0) {
            const productSummary = order.cartItemDetails.map(item => item.count + 'x ' + item.itemName).join(', ');
            content.push({ text: productSummary, style: 'small', color: 'var(--bo-muted-2)', margin: [0, 0, 0, 5] });
          }

          // Calculation breakdown
          const calcRows = [];
          calcRows.push([{ text: 'Basis hentetid', style: 'tableCell' }, { text: calc.basePickupTime + ' min', style: 'tableCell', alignment: 'right' }]);
          calcRows.push([{ text: 'Kjøkken tilberedningstid', style: 'tableCell' }, { text: calc.kitchenPrepTime + ' min', style: 'tableCell', alignment: 'right' }]);
          if (calc.totalPrepTimeMinutes !== undefined) {
            calcRows.push([{ text: '  Fra prepTimeMinutes', style: 'smallCell' }, { text: calc.totalPrepTimeMinutes + ' min', style: 'smallCell', alignment: 'right' }]);
          }
          if (calc.priceBasedPrepTime !== undefined) {
            calcRows.push([{ text: '  Fra pris (' + calc.orderValueWithoutPrepTime + ' kr / ' + calc.krPerMinute + ' kr/min)', style: 'smallCell' }, { text: calc.priceBasedPrepTime + ' min', style: 'smallCell', alignment: 'right' }]);
          }
          if (calc.fallbackPriceBasedPrepTime !== undefined) {
            calcRows.push([{ text: '  Fallback pris-basert', style: 'smallCell' }, { text: calc.fallbackPriceBasedPrepTime + ' min', style: 'smallCell', alignment: 'right' }]);
          }
          if (calc.readyMadePrepTimeMinutes > 0) {
            calcRows.push([{ text: 'ReadyMade tilberedningstid', style: 'tableCell' }, { text: calc.readyMadePrepTimeMinutes + ' min', style: 'tableCell', alignment: 'right' }]);
          }
          calcRows.push([{ text: 'Har kjøkkenvarer', style: 'tableCell' }, { text: calc.hasKitchenItems ? 'Ja' : 'Nei', style: 'tableCell', alignment: 'right' }]);
          calcRows.push([{ text: 'Køverdi ved bestillingstidspunkt', style: 'tableCell' }, { text: calc.queueValueAtOrderTimeKr + ' kr', style: 'tableCell', alignment: 'right' }]);
          calcRows.push([{ text: 'Køventetid', style: 'tableCell' }, { text: calc.queueWaitMinutes + ' min', style: 'tableCell', alignment: 'right' }]);
          if (calc.formula) {
            calcRows.push([{ text: 'Formel', style: 'tableCell' }, { text: calc.formula, style: 'smallCell', alignment: 'right' }]);
          }
          calcRows.push([{ text: 'Før avrunding', style: 'tableCell' }, { text: calc.beforeRounding + ' min', style: 'tableCell', alignment: 'right' }]);
          calcRows.push([{ text: 'Beregnet hentetid', style: 'tableBold', fillColor: '#ecfdf5' }, { text: calc.afterRounding + ' min', style: 'tableBold', alignment: 'right', fillColor: '#ecfdf5' }]);
          calcRows.push([{ text: 'Faktisk ventetid', style: 'tableBold', fillColor: order.actualWaitMinutes !== calc.afterRounding ? '#fef2f2' : null }, { text: order.actualWaitMinutes + ' min', style: 'tableBold', alignment: 'right', fillColor: order.actualWaitMinutes !== calc.afterRounding ? '#fef2f2' : null }]);

          content.push({
            table: { widths: ['*', 'auto'], body: calcRows },
            layout: 'lightHorizontalLines',
            margin: [0, 0, 0, 2]
          });

          // Slot check
          if (calc.slotCheck) {
            const slot = calc.slotCheck;
            content.push({
              table: {
                widths: ['*', 'auto'],
                body: [
                  [{ text: 'Slot', style: 'tableCell' }, { text: this.formatTime(slot.slotStart) + ' – ' + this.formatTime(slot.slotEnd), style: 'tableCell', alignment: 'right' }],
                  [{ text: 'Verdi i slot', style: 'tableCell' }, { text: slot.slotValueKr + ' kr', style: 'tableCell', alignment: 'right' }],
                  [{ text: 'Slot-kapasitet', style: 'tableCell' }, { text: slot.slotCapacityKr + ' kr', style: 'tableCell', alignment: 'right' }],
                  [{ text: 'Utnyttelse', style: 'tableCell' }, { text: slot.slotUtilizationPercent + '%', style: 'tableCell', alignment: 'right' }],
                  [{ text: 'Overbelastet', style: slot.overloaded ? 'tableBold' : 'tableCell', fillColor: slot.overloaded ? '#fef2f2' : null }, { text: slot.overloaded ? 'JA' : 'Nei', style: slot.overloaded ? 'tableBold' : 'tableCell', alignment: 'right', fillColor: slot.overloaded ? '#fef2f2' : null }]
                ]
              },
              layout: 'lightHorizontalLines',
              margin: [0, 0, 0, 2]
            });
          }

          // Cart items
          if (order.cartItemDetails && order.cartItemDetails.length > 0) {
            const cartBody = [
              [
                { text: 'Produkt', style: 'tableBold' },
                { text: 'Ant.', style: 'tableBold', alignment: 'right' },
                { text: 'Pris', style: 'tableBold', alignment: 'right' },
                { text: 'Beregningsmetode', style: 'tableBold' },
                { text: 'Bidrag', style: 'tableBold', alignment: 'right' }
              ]
            ];
            for (const item of order.cartItemDetails) {
              cartBody.push([
                { text: item.itemName, style: 'tableCell' },
                { text: String(item.count), style: 'tableCell', alignment: 'right' },
                { text: item.price + ' kr', style: 'tableCell', alignment: 'right' },
                { text: this.getCategoryLabel(item.category), style: 'tableCell' },
                { text: item.contributionMinutes + ' min', style: 'tableCell', alignment: 'right' }
              ]);
            }
            content.push({
              table: { headerRows: 1, widths: ['*', 'auto', 'auto', 'auto', 'auto'], body: cartBody },
              layout: 'lightHorizontalLines',
              margin: [0, 2, 0, 0]
            });
          }

          content.push(separator);
        }

        const docDefinition = {
          pageSize: 'A4',
          pageMargins: [40, 40, 40, 40],
          content: content,
          styles: {
            title: { fontSize: 14, bold: true },
            sectionTitle: { fontSize: 11, bold: true },
            tableBold: { fontSize: 8, bold: true },
            tableCell: { fontSize: 8 },
            smallCell: { fontSize: 7, color: 'var(--bo-muted)' },
            small: { fontSize: 8, color: 'var(--bo-muted)' }
          },
          defaultStyle: { fontSize: 9 }
        };

        const locationName = (config.locationName || 'location').replace(/\s+/g, '_');
        const fileName = 'Smart_hentetid_' + locationName + '_' + this.queriedFromDate + '.pdf';
        pdfMake.createPdf(docDefinition).download(fileName);
      }
    }
  };
</script>

<style scoped>
/* Config panel */
.config-panel {
  background: var(--bo-surface-2);
  border-radius: 10px;
  padding: 20px;
  border: 1px solid var(--bo-line);
}

.config-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px 24px;
  margin-top: 12px;
}

@media (min-width: 768px) {
  .config-grid { grid-template-columns: repeat(4, 1fr); }
}

.config-item {
  display: flex;
  flex-direction: column;
}

.config-item-wide {
  grid-column: span 2;
}

.config-label {
  font-size: 12px;
  color: var(--bo-muted-2);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.config-value {
  font-size: 15px;
  font-weight: 600;
  color: var(--bo-ink);
}

/* Order list */
.order-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.order-card {
  background: var(--bo-surface);
  border-radius: 8px;
  border: 1px solid var(--bo-line);
  border-left: 4px solid var(--bo-muted-2);
  overflow: hidden;
}

.border-status-ok { border-left-color: #22c55e; }
.border-status-adjusted { border-left-color: #f59e0b; }
.border-status-overloaded { border-left-color: #ef4444; }

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  user-select: none;
}

.order-header:hover {
  background: var(--bo-surface-2);
}

.order-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.order-header-right {
  display: flex;
  align-items: center;
  gap: 14px;
}

.order-id {
  font-weight: 600;
  font-size: 14px;
  color: var(--bo-ink);
}

.badge-channel, .badge-type {
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 10px;
  background: var(--bo-line);
  color: var(--bo-ink);
}

.order-amount {
  font-weight: 700;
  font-size: 15px;
  color: var(--bo-ink);
}

.order-wait {
  font-size: 13px;
  color: var(--bo-muted);
}

.expand-icon {
  font-size: 11px;
  color: var(--bo-muted-2);
}

.order-times {
  display: flex;
  gap: 20px;
  padding: 0 16px 4px;
  font-size: 13px;
  color: var(--bo-muted);
}

.order-products {
  padding: 0 16px 10px;
  font-size: 12px;
  color: var(--bo-muted-2);
}

/* Expanded detail */
.order-detail {
  padding: 16px;
  border-top: 1px solid var(--bo-surface-2);
  background: var(--bo-surface-2);
}

/* Calculation grid */
.calc-section h6, .cart-section h6, .slot-section h6 {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--bo-muted);
  margin-bottom: 8px;
}

.calc-grid {
  display: flex;
  flex-direction: column;
}

.calc-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  border-bottom: 1px solid var(--bo-surface-2);
  font-size: 14px;
}

.calc-label {
  color: var(--bo-muted);
}

.calc-value {
  font-weight: 500;
  color: var(--bo-ink);
}

.calc-sub .calc-label {
  padding-left: 16px;
  font-size: 13px;
  color: var(--bo-muted-2);
}

.calc-formula {
  background: var(--bo-info-bg);
}

.calc-formula .calc-value {
  font-family: monospace;
  font-size: 13px;
}

.calc-result {
  background: var(--bo-ok-bg);
  font-weight: 600;
}

.calc-result .calc-value {
  color: var(--bo-ok-fg);
  font-weight: 700;
}

.calc-mismatch {
  background: var(--bo-bad-bg);
}

.calc-mismatch .calc-value {
  color: var(--bo-alert-ink);
  font-weight: 600;
}

/* Slot bar */
.slot-bar-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.slot-bar-bg {
  flex: 1;
  height: 20px;
  background: var(--bo-line);
  border-radius: 10px;
  overflow: hidden;
}

.slot-bar-fill {
  height: 100%;
  background: #22c55e;
  border-radius: 10px;
  transition: width 0.3s;
}

.slot-overloaded {
  background: #ef4444;
}

.slot-bar-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--bo-ink);
  min-width: 40px;
}

/* Cart items table */
.cart-table {
  width: 100%;
  font-size: 13px;
  border-collapse: collapse;
}

.cart-table th {
  text-align: left;
  padding: 6px 8px;
  border-bottom: 2px solid var(--bo-line);
  font-size: 12px;
  text-transform: uppercase;
  color: var(--bo-muted);
  letter-spacing: 0.3px;
}

.cart-table td {
  padding: 5px 8px;
  border-bottom: 1px solid var(--bo-surface-2);
}

.item-category-badge {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 8px;
}

.badge-kitchenPrepTime { background: var(--bo-warn-bg); color: var(--bo-warn-fg); }
.badge-readyMade { background: var(--bo-ok-bg); color: var(--bo-ok-fg); }
.badge-priceBasedEstimate { background: var(--bo-info-bg); color: #3730a3; }

.text-warning { color: #f59e0b !important; }
.text-danger { color: #ef4444 !important; }

.pdf-icon {
  display: inline-flex !important;
  align-items: center;
  margin-top: 0 !important;
  text-align: left !important;
  clear: none !important;
  cursor: pointer;
}

.filter-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 16px;
}

@media (min-width: 768px) {
  .filter-grid {
    grid-template-columns: repeat(2, 1fr) auto;
  }
}

@media (min-width: 1200px) {
  .filter-grid {
    grid-template-columns: minmax(200px, 280px) minmax(150px, 200px) minmax(150px, 200px) auto;
  }
}

.filter-label {
  display: block;
  font-weight: bold;
  margin-bottom: 8px;
}

.filter-item :deep(.mt-4) {
  margin-top: 0 !important;
}

.filter-item :deep(.mb-4) {
  margin-bottom: 0 !important;
}

.filter-item :deep(.ingredient-title) {
  display: none;
}

.date-time-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-btn-item {
  display: flex;
  align-items: flex-end;
}

.filter-btn-item .bo_btn {
  height: 38px;
}
</style>
